import EditHeader from "common/components/EditHeader/EditHeader";
import HeaderButtonsEdit from "common/components/EditHeader/HeaderButtonsEdit";
import FormControl from "common/components/FormControl/FormControl";
import FormScrollError from "common/components/FormScrollError/FormScrollError";
import useRouter from "common/hooks/use-router";
import usePark from "common/hooks/usePark";
import { useContext, useEffect, useMemo, useState } from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import arrayMutators from "final-form-arrays";
import imgSizes from "conf/imgSizes.json";
import useAuth from "common/hooks/useAuth";
import ToggleSwitch from "common/components/ToggleSwitch/ToggleSwitch";
import dayjs from "dayjs";
import SectionTitle from "common/components/SectionTitle/SectionTitle";
import rolesData from "conf/roleTypesData";
import LanguageSelect from "components/select/LanguageSelect/LanguageSelect";
import RadioBlock from "common/components/RadioBlock/RadioBlock";
import i18n from "i18n";
import TabBlockItem from "common/components/TabBlockItem/TabBlockItem";
import Icon, { IconNames } from "components/Icon/Icon";
import { DE, ES, FR, GB } from "country-flag-icons/react/3x2";
import { Constants } from "constants/Constants";
import { getStoredData } from "common/utils/fnAsyncStorage";
import {
  checkCsvConsistency,
  CsvValidationError,
} from "common/utils/checkCsvConsistency";
import clsx from "clsx";
import AlertsContext from "common/providers/alerts";
import { csv2json } from "common/utils/csv2json";

function ParkForm({ onSubmit, park }: any) {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState("");
  const { navigate } = useRouter();
  const { park: currentPark } = usePark();
  const { user: currentUser } = useAuth();
  const [translationLanguage, setTranslationLanguage] = useState(i18n.language);
  const [csvErrorMessages, setCsvErrorMessages] = useState<
    CsvValidationError[]
  >([]);

  // État pour le toggle de limitation des utilisateurs
  const [isLimitEnabled, setIsLimitEnabled] = useState(
    park?.maxNbUsers !== undefined && park?.maxNbUsers !== null
  );

  // Synchroniser l'état du toggle quand le park change
  useEffect(() => {
    setIsLimitEnabled(
      park?.maxNbUsers !== undefined && park?.maxNbUsers !== null
    );
  }, [park?.id, park?.maxNbUsers]);

  const { setAlert } = useContext(AlertsContext);

  const canManagePark =
    (currentUser?.isAdmin ||
      currentUser?.isSuperadmin ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        currentUser?.administrationPermissions?.myPark?.update)) ??
    false;
  const canManageTestAspect = currentUser?.isSuperadmin;

  const languages: {
    id: string;
    name: string;
    icon: IconNames | React.ReactElement | React.ComponentType<any>;
    color: string;
  }[] = useMemo(() => {
    const className = "h-[1em] mr-2";
    const color = "secondary";
    return [
      {
        id: "en",
        name: t("language.en"),
        icon: <GB className={className} />,
        color,
      },
      {
        id: "fr",
        name: t("language.fr"),
        icon: <FR className={className} />,
        color,
      },
      {
        id: "de",
        name: t("language.de"),
        icon: <DE className={className} />,
        color,
      },
      {
        id: "es",
        name: t("language.es"),
        icon: <ES className={className} />,
        color,
      },
    ];
  }, [t]);

  const currentLanguage = useMemo(() => {
    return languages.find((l) => l.id === translationLanguage);
  }, [translationLanguage, languages]);

  const initialValues = useMemo(
    () =>
      park?.id
        ? {
            ...park,
          }
        : {
            name: "",
            front_url: import.meta.env.VITE_FRONT_URL,
            isPublicByDefault: true,
            limitTestDate: dayjs().startOf("day"),
            language: "en",
            hasModifications: true,
            hasTrainings: true,
            hasEnrichments: true,
            hasUnavailabilities: true,
            hasAssignments: true,
            hasZimsImport: true,
            customTranslations: {},
            maxNbUsers: null,
          },
    [park]
  );

  const onSubmitWrapper = async (values: any) => {
    const valuesToSend = { ...values };
    // delete valuesToSend.customTranslations;

    // Convertir valeur vide/undefined de maxNbUsers en null
    if (
      valuesToSend.maxNbUsers === "" ||
      valuesToSend.maxNbUsers === undefined
    ) {
      valuesToSend.maxNbUsers = null;
    }

    if (valuesToSend.limitTestDate && !valuesToSend.id) {
      // eslint-disable-next-line no-param-reassign
      valuesToSend.limitTestDate = dayjs(valuesToSend.limitTestDate).add(
        1,
        "day"
      );
    }
    const res = await onSubmit(valuesToSend);

    if (res.message) {
      setErrorMessage(res.message);
    }
  };

  const onDownloadTranslation = async () => {
    if (!translationLanguage || !park) return;

    const token = await getStoredData("token");
    const apiUrl = `${Constants.API_URL}/parks/translations/${translationLanguage}/${park.id}`;

    const headers = new Headers();
    headers.append("Authorization", `Bearer ${token}`);

    const res = await fetch(apiUrl, { headers });
    if (!res.ok) throw new Error("Download failed");

    const a = document.createElement("a");
    document.body.appendChild(a);

    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    a.href = url;
    a.download = `${translationLanguage}.csv`;
    a.click();

    setTimeout(() => URL.revokeObjectURL(url), 1000);
    a.remove();
  };

  const onUploadTranslation = async (file: File, form: any) => {
    if (!translationLanguage || !park) return;

    setCsvErrorMessages([]);

    // check CSV consistency
    const { errors } = await checkCsvConsistency(file);

    if (errors.length > 0) {
      console.error("CSV consistency check failed", errors);
      setCsvErrorMessages(errors);
      // You can display errors to the user here
      return;
    }

    // get file content as json
    try {
      // Parse CSV file using csv2json utility
      const records = await csv2json(file, "originalString");

      // update the customTranslation property with file content (for the right lang)
      form.mutators.setFieldValue({
        field: `customTranslations.${translationLanguage}`,
        value: records,
      });

      setAlert(t("import.translationUploaded"), "success");
    } catch (error) {
      console.error("Error parsing CSV file:", error);
      setAlert(t("import.translationUploadError"), "error");
    }
  };

  return (
    <Form
      onSubmit={onSubmitWrapper}
      subscription={{ values: true, submitting: true, pristine: true }}
      initialValues={initialValues}
      mutators={{
        ...arrayMutators,
        setFieldValue: ([field]: any, state: any, utils: any) => {
          utils.changeValue(state, field.field, () => field.value);
        },
      }}
      render={({ handleSubmit, values, form }) => {
        return (
          <div className=" mx-auto max-w-full py-4 px-4 text-slate-500 text-base">
            {/* Header */}
            <EditHeader
              title={t(
                currentPark?.id
                  ? "editItem.myPark"
                  : park?.id
                  ? "editItem.park"
                  : "newItem.park"
              )}
              subtitle={values.name}
              icon="BuildingOfficeIcon"
              errorMessage={errorMessage}
              HeaderButtonsEdit={
                <HeaderButtonsEdit
                  inHeader
                  onSubmit={canManagePark ? handleSubmit : undefined}
                  onCancel={currentPark?.id ? null : () => navigate("/parks")}
                />
              }
            />
            <form onSubmit={handleSubmit} noValidate>
              <FormScrollError />

              <div className="responsive-flex-lg gap-2 mt-2 ">
                <div className="lg:flex-1 flex flex-col gap-2">
                  <div className="flex flex-row gap-4 white-box ">
                    <div className="responsive-flex gap-4 ">
                      <FormControl
                        name="logo"
                        title={t("forms.logo")}
                        type="image"
                        maxWidth={imgSizes.logo.width}
                        maxHeight={imgSizes.logo.height}
                        labelIcon="PhotoIcon"
                        editable={canManagePark}
                        disabled={!canManagePark}
                        className="border border-slate-200 rounded-lg overflow-hidden"
                      />
                    </div>

                    <div className="flex flex-col w-full gap-2 ">
                      <FormControl
                        name="name"
                        title={t("common.name")}
                        editable={currentUser?.isSuperadmin}
                        required
                      />
                      <FormControl
                        name="language"
                        title={t("forms.parkLanguage")}
                        type="custom"
                        editable={currentUser?.isSuperadmin}
                        required
                      >
                        <LanguageSelect />
                      </FormControl>
                      {canManageTestAspect ? (
                        <FormControl
                          type="custom"
                          name="isTest"
                          editable={canManageTestAspect}
                        >
                          <ToggleSwitch
                            isDisabled={!canManageTestAspect}
                            rightLabel={t("park.test")}
                          />
                        </FormControl>
                      ) : values.isTest ? (
                        <p>
                          {t("park.testAccess", {
                            date: dayjs(park.limitTestDate).format("LL"),
                          })}
                        </p>
                      ) : null}
                      {canManageTestAspect && values.isTest && (
                        <FormControl
                          name="limitTestDate"
                          title={t("park.limitTestDate")}
                          editable={canManagePark}
                          type="date"
                          showTimeSelect={false}
                          showYearDropdown
                          required
                          containerStyle="w-1/3 min-w-[100px]"
                          locale={currentUser?.language ?? "fr"}
                        />
                      )}
                      {canManageTestAspect && park?.additionalData ? (
                        <div className="flex flex-col white-box gap-3">
                          <SectionTitle title="Comptes de test" />
                          {park?.additionalData?.map((account: any) => {
                            return (
                              <div key={account.id}>
                                <span className="font-bold text-lg">
                                  {t(
                                    rolesData.find((r) => r.id === account.role)
                                      ?.titleKey ?? ""
                                  )}
                                  <br />
                                </span>
                                <span className="font-semibold">
                                  {t("park.username")}
                                  <br />
                                </span>
                                <span>
                                  {account.username}
                                  <br />
                                </span>
                                <span className="font-semibold">
                                  {t("park.password")}
                                  <br />
                                </span>
                                <span>{account.password}</span>
                                <br />
                                <br />
                              </div>
                            );
                          })}
                        </div>
                      ) : null}

                      <div className="flex flex-col mt-4 gap-3">
                        {currentUser?.isSuperadmin ? (
                          <>
                            <SectionTitle
                              title={t(
                                "administrationPermissions.activationFeatures"
                              )}
                              className="mt-4"
                            />
                            <div className="grid grid-cols-2 gap-2">
                              <FormControl
                                name="hasModifications"
                                editable={currentUser?.isSuperadmin}
                                type="custom"
                              >
                                <ToggleSwitch
                                  isDisabled={!currentUser?.isSuperadmin}
                                  rightLabel={t("forms.parkHasModifications")}
                                />
                              </FormControl>
                              <FormControl
                                name="hasTrainings"
                                editable={currentUser?.isSuperadmin}
                                type="custom"
                              >
                                <ToggleSwitch
                                  isDisabled={!currentUser?.isSuperadmin}
                                  rightLabel={t("forms.parkHasTrainings")}
                                />
                              </FormControl>
                              <FormControl
                                name="hasEnrichments"
                                editable={currentUser?.isSuperadmin}
                                type="custom"
                              >
                                <ToggleSwitch
                                  isDisabled={!currentUser?.isSuperadmin}
                                  rightLabel={t("forms.parkHasEnrichments")}
                                />
                              </FormControl>
                              <FormControl
                                name="hasUnavailabilities"
                                editable={currentUser?.isSuperadmin}
                                type="custom"
                              >
                                <ToggleSwitch
                                  isDisabled={!currentUser?.isSuperadmin}
                                  rightLabel={t(
                                    "forms.parkHasUnavailabilities"
                                  )}
                                />
                              </FormControl>
                              <FormControl
                                name="hasAssignments"
                                editable={currentUser?.isSuperadmin}
                                type="custom"
                              >
                                <ToggleSwitch
                                  isDisabled={!currentUser?.isSuperadmin}
                                  rightLabel={t("forms.parkHasAssignments")}
                                />
                              </FormControl>
                              <FormControl
                                name="hasZimsImport"
                                editable={currentUser?.isSuperadmin}
                                type="custom"
                              >
                                <ToggleSwitch
                                  isDisabled={!currentUser?.isSuperadmin}
                                  rightLabel={t("forms.parkHasZimsImport")}
                                />
                              </FormControl>
                            </div>
                          </>
                        ) : null}
                        {currentUser?.isSuperadmin || currentUser?.isAdmin ? (
                          <>
                            <SectionTitle
                              title={t(
                                "administrationPermissions.userAccessControl"
                              )}
                              className="mt-6"
                            />
                            <ToggleSwitch
                              isDisabled={!currentUser?.isSuperadmin}
                              rightLabel={t(
                                "administrationPermissions.limitNbUsers"
                              )}
                              value={isLimitEnabled}
                              onChange={(value: boolean) => {
                                setIsLimitEnabled(value);
                                if (value) {
                                  // Activer: définir à 1 si vide, sinon garder la valeur actuelle
                                  const currentValue = values.maxNbUsers;
                                  if (
                                    currentValue === null ||
                                    currentValue === undefined ||
                                    currentValue === ""
                                  ) {
                                    form.change("maxNbUsers", 1);
                                  }
                                } else {
                                  // Désactiver: mettre à null (illimité)
                                  form.change("maxNbUsers", null);
                                }
                              }}
                            />
                            {isLimitEnabled && (
                              <FormControl
                                name="maxNbUsers"
                                title={t(
                                  "administrationPermissions.maxNbUsers"
                                )}
                                editable={currentUser?.isSuperadmin}
                                type="number"
                                inputContainerStyle="w-48"
                                required={isLimitEnabled}
                              />
                            )}
                          </>
                        ) : null}
                        {currentUser?.isSuperadmin || currentUser?.isAdmin ? (
                          <>
                            <SectionTitle
                              title={t(
                                "administrationPermissions.customTranslations"
                              )}
                              className="mt-6"
                            />
                            <div className="rounded-lg px-3">
                              <RadioBlock
                                Component={TabBlockItem}
                                value={translationLanguage}
                                onChange={(id) => {
                                  setTranslationLanguage(`${id}`);
                                }}
                                items={languages}
                                horizontal
                                noGap
                              />
                              <div className="flex flex-col gap-4 px-8 py-4 w-max">
                                <button
                                  type="button"
                                  className="w-full inline-flex items-center rounded-md border border-yellow-600 shadow-sm px-4 py-2 disabled:bg-gray-50 text-base font-medium text-yellow-600 hover:bg-yellow-600 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 sm:text-sm"
                                  onClick={onDownloadTranslation}
                                >
                                  <Icon
                                    name="BiDownload"
                                    className="h-6 w-6 mr-2"
                                  />
                                  {t("forms.customTranslation.download", {
                                    language: currentLanguage?.name,
                                  })}
                                </button>

                                <label
                                  className={clsx(
                                    "w-full inline-flex items-center rounded-md border shadow-sm px-4 py-2 bg-white cursor-pointer text-base font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm hover:bg-yellow-600 hover:text-white ",
                                    csvErrorMessages?.length
                                      ? "border-red-600 text-red-600 focus:ring-red-500"
                                      : "border-yellow-600 text-yellow-600 focus:ring-yellow-500"
                                  )}
                                >
                                  <Icon
                                    name="BiUpload"
                                    className="h-6 w-6 mr-2"
                                  />
                                  {t("forms.customTranslation.upload", {
                                    language: currentLanguage?.name,
                                  })}

                                  <input
                                    type="file"
                                    accept=".csv"
                                    className="hidden"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) onUploadTranslation(file, form);
                                      e.currentTarget.value = ""; // allow re-upload same file
                                    }}
                                  />
                                </label>
                              </div>
                              {csvErrorMessages?.length ? (
                                <div className="text-red-600 text-sm flex flex-col">
                                  <div className="font-bold">
                                    {t("import.csvConsistencyCheckFailed", {
                                      count: csvErrorMessages.length,
                                    })}
                                  </div>
                                  <ul>
                                    {csvErrorMessages.map((error) => (
                                      <li
                                        className="ml-6 italic list-disc"
                                        key={error.line}
                                      >
                                        {t("import.csvError", {
                                          type: error.type,
                                          line: error.line,
                                          message: error.message,
                                        })}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              ) : null}
                            </div>
                          </>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <HeaderButtonsEdit
                onSubmit={handleSubmit}
                onCancel={() => navigate("/parks")}
              />
            </form>
          </div>
        );
      }}
    />
  );
}

export default ParkForm;
